/**
 * Shared Slack API helper functions for sending messages and uploading files.
 * These functions call the Admin Portal's Slack proxy routes.
 */

/**
 * Sends a message to a Slack channel via the Admin Portal proxy.
 * @param channel - The channel key configured in src/server/routes/slack.js (e.g., 'bulk-credits', 'bulk-vcc-replace')
 * @param message - The message text to send (supports Slack markdown)
 */
export async function postSlackMessage(channel: string, message: string): Promise<void> {
  const response = await fetch('/api/slack/send-message', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      channel,
      message,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send Slack message');
  }
}

/**
 * Uploads a file to a Slack channel via the Admin Portal proxy.
 * @param channel - The channel key configured in src/server/routes/slack.js (e.g., 'bulk-credits', 'bulk-vcc-replace')
 * @param fileContent - The file content as a string (e.g., CSV data)
 * @param filename - The filename for the upload
 * @param message - The message to accompany the file upload
 */
export async function uploadSlackFile(
  channel: string,
  fileContent: string,
  filename: string,
  message: string,
): Promise<void> {
  const response = await fetch('/api/slack/upload-file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      channel,
      fileContent,
      filename,
      message,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload Slack file');
  }
}
